import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ClipboardCheck,
  Radio,
  CandlestickChart,
  Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/site/Layout";

const CANONICAL = "https://xauproacademy.com/validation";
const TITLE = "Validation Hub: How Every Gold Trade Is Verified | XAU Pro Academy";
const DESC =
  "How XAU Pro Academy verifies every XAUUSD decision: rule-based qualification, live price entry capture, candle by candle resolution, and a permanent public archive.";

export const Route = createFileRoute("/validation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: ValidationHubPage,
});

const RECORD = [
  { label: "Verified trades", value: "241" },
  { label: "Net result", value: "+95.94R" },
  { label: "Profit factor", value: "1.72" },
  { label: "Max drawdown", value: "8.25R" },
];

const STEPS = [
  {
    icon: ClipboardCheck,
    title: "Rule-based qualification",
    body: "A setup is considered only when fixed structure, session, and risk rules are met. Most candidates are declined.",
  },
  {
    icon: Radio,
    title: "Entry from a live price",
    body: "Entries are recorded from a real-time quote at the moment of execution, not from a candle reconstructed later.",
  },
  {
    icon: CandlestickChart,
    title: "Resolved candle by candle",
    body: "Stops and targets are resolved against recorded price data. The outcome is whatever the market printed.",
  },
  {
    icon: Archive,
    title: "Archived permanently",
    body: "Every closed trade, winning or losing, is published in Case Studies with a full chart replay.",
  },
];

const RULES = [
  { title: "Append only", body: "Closed trades are never edited or removed. Losses stay in the record." },
  { title: "Reproducible", body: "Every figure is derived from stored trade and price records. If it cannot be reproduced, it is not shown." },
  { title: "Denominators visible", body: "Results are always shown with their sample size and period." },
  { title: "Honest degradation", body: "Missing or stale data is shown as pending, never inferred or smoothed." },
  { title: "No forced trades", body: "Days without a qualified setup produce no trade. Frequency is never a target." },
];

function ValidationHubPage() {
  return (
    <Layout>
      <section className="border-b border-border/60 bg-gradient-hero">
        <div className="container mx-auto max-w-5xl px-4 py-16 md:py-20">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
            Validation Hub
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold leading-tight md:text-5xl">
            The platform measures itself.
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Every XAUUSD decision follows the same path: qualified by rules,
            entered at a live price, resolved against real market data, and
            archived publicly. This page explains that path.
          </p>
          <p className="mt-4 max-w-2xl text-xs uppercase tracking-wider text-muted-foreground">
            Not a broker. Not a prop firm. For educational purposes only.
          </p>
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Verified record, 2024 to 2026
        </div>
        <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60 md:grid-cols-4">
          {RECORD.map((r) => (
            <div key={r.label} className="bg-card p-5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {r.label}
              </div>
              <div className="mt-2 font-display text-2xl font-bold text-gold">{r.value}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to="/track-record">
              View the Performance Record <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/case-studies">Browse Case Studies</Link>
          </Button>
        </div>
      </section>

      <section className="border-y border-border/60 bg-background">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
            Verification path
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">
            How a trade is verified.
          </h2>
          <ol className="mt-8 grid gap-4 md:grid-cols-2">
            {STEPS.map((s, i) => (
              <li key={s.title} className="rounded-lg border border-border/60 bg-card p-5">
                <div className="flex items-center gap-3">
                  <s.icon className="h-4 w-4 text-gold" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-base font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-16">
        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
          Integrity rules
        </div>
        <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">
          How these numbers are kept honest.
        </h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {RULES.map((r) => (
            <li key={r.title} className="rounded-md border border-border/60 bg-card/60 p-4">
              <div className="text-sm font-semibold text-foreground">{r.title}</div>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">
            Out of scope
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">
            What this page does not claim.
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Past results do not predict future performance. Figures are shown
            in R (multiples of risk) before costs, and are not a promise of
            returns. Full trade-level results are listed on the Performance
            Record.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/track-record">
                View the Performance Record <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/orion/how-it-works">How the engine works</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
